/**
 * NDT / Inspections (vertical key `ndt_inspection`) — batch 1 shapes, typed by
 * hand from fieldiq-api's route files at 0a205a7 (api#1134):
 * ndt-inspection.routes.ts, ndt-inspection-people.routes.ts,
 * ndt-inspection-radiation.routes.ts. The recorded manifest holds only 403s
 * for these routes (the probe tenant does not store the key), so nothing here
 * is generated.
 *
 * READS ARE camelCase — unlike Pressure Testing and Downhole, whose reads are
 * snake_case. Do not copy a PT field name here. WRITES take snake_case keys,
 * with lengths, activity and dose in the tenant's units (`meta.units`); the API
 * converts both ways, so nothing on this side converts.
 */

export interface NdtUnits {
  length: 'mm' | 'in';
  activity: 'GBq' | 'Ci';
  dose: 'mSv' | 'mrem';
}

export interface NdtMeta {
  unitSystem: 'metric' | 'imperial' | null;
  unitSystemSource: 'company' | 'country_fallback' | null;
  units: NdtUnits | null;
  currency: string | null;
  timeZone: string | null;
  /** The tenant's today, YYYY-MM-DD. */
  today: string | null;
  calWarnDays: number[] | null;
  expiryWarnDays: number[] | null;
}

/** Every /ndt-inspection response. Read with `unwrapNdt` (hooks/useNdtInspection.ts). */
export interface NdtEnvelope<T> {
  success: boolean;
  data: T | null;
  meta?: Partial<NdtMeta>;
  error?: string;
  code?: string;
}

export interface NdtResult<T> {
  data: T;
  meta: NdtMeta;
}

export interface NdtCatalogEntry {
  value: string;
  label: string;
}

export interface NdtCatalog {
  codes: NdtCatalogEntry[];
  methods: NdtCatalogEntry[];
  levellessMethods: string[];
  schemes: NdtCatalogEntry[];
  weldProcesses: NdtCatalogEntry[];
  weldPositions: NdtCatalogEntry[];
  progressions: NdtCatalogEntry[];
  instrumentKinds: string[];
  dosimeterKinds: string[];
  sourceStatuses: string[];
  sourceEvents: string[];
  isotopes: { value: string; label: string; halfLifeDays: number }[];
}

export type NdtTicketMode = 'per_day' | 'per_job';

export interface NdtSettings {
  defaultTicketMode: NdtTicketMode;
  defaultAcceptanceCode: string | null;
  calWarnDays: number[];
  expiryWarnDays: number[];
  doseInvestigationLevelMsv: number | null;
  /** The same level in the tenant's dose unit. */
  doseInvestigationLevel: number | null;
  configured: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtSettingsUpdate {
  default_ticket_mode?: NdtTicketMode;
  default_acceptance_code?: string | null;
  cal_warn_days?: number[];
  expiry_warn_days?: number[];
  /** In the tenant's dose unit (mSv or mrem). */
  dose_investigation_level?: number | null;
}

/** Expiry standing of a cert, a qualification or a leak test. Computed by the API against the tenant's today. */
export const NDT_EXPIRY_STATUSES = ['valid', 'due_soon', 'expired', 'none'] as const;
export type NdtExpiryStatus = (typeof NDT_EXPIRY_STATUSES)[number];

/**
 * A welder qualification's standing (fieldiq-api lib/ndt-status.ts qualState):
 * the expiry vocabulary plus `lapsed`, so the two causes never share a word —
 * `expired` = the expiry day has passed, `lapsed` = process continuity has
 * (the expiry may still be valid). `none` = no expiry recorded, continuity holding.
 */
export const NDT_WELDER_QUAL_STATUSES = ['valid', 'due_soon', 'lapsed', 'expired', 'none'] as const;
export type NdtWelderQualStatus = (typeof NDT_WELDER_QUAL_STATUSES)[number];
/** Process continuity alone: it lapses, it never expires. */
export type NdtContinuityStatus = 'valid' | 'due_soon' | 'lapsed';

export interface NdtTechnicianCert {
  id: string;
  userId: string;
  userName: string | null;
  method: string;
  methodLabel: string | null;
  level: number | null;
  scheme: string;
  schemeLabel: string | null;
  sector: string | null;
  certificateNumber: string | null;
  issuingBody: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  documentUploadId: string | null;
  notes: string | null;
  status: NdtExpiryStatus;
  daysLeft: number | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtCertWrite {
  user_id?: string;
  method?: string;
  level?: number | null;
  scheme?: string;
  sector?: string | null;
  certificate_number?: string | null;
  issuing_body?: string | null;
  issued_on?: string | null;
  expires_on?: string | null;
  notes?: string | null;
  document_upload_id?: string | null;
}

export interface NdtWelderQual {
  id: string;
  welderId: string;
  code: string;
  codeLabel: string | null;
  process: string;
  processLabel: string | null;
  position: string | null;
  progression: string | null;
  fillerClass: string | null;
  diameterMin: number | null;
  diameterMax: number | null;
  wallMin: number | null;
  wallMax: number | null;
  qualifiedOn: string;
  expiresOn: string | null;
  continuityOn: string | null;
  continuityLapsesOn: string | null;
  status: NdtWelderQualStatus;
  daysLeft: number | null;
  expiryStatus: NdtExpiryStatus;
  continuityStatus: NdtContinuityStatus;
  testReportRef: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtQualWrite {
  code?: string;
  process?: string;
  position?: string | null;
  progression?: string | null;
  filler_class?: string | null;
  diameter_min?: number | null;
  diameter_max?: number | null;
  wall_min?: number | null;
  wall_max?: number | null;
  qualified_on?: string;
  expires_on?: string | null;
  continuity_on?: string | null;
  test_report_ref?: string | null;
  notes?: string | null;
}

export interface NdtWelder {
  id: string;
  name: string;
  welderStamp: string | null;
  employer: string | null;
  userId: string | null;
  userName: string | null;
  notes: string | null;
  qualCount: number;
  currentQualCount: number;
  /** The welder's worst current standing; null when they hold no qualification. */
  qualStatus: NdtWelderQualStatus | null;
  quals: NdtWelderQual[];
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtWelderWrite {
  name?: string;
  welder_stamp?: string | null;
  employer?: string | null;
  user_id?: string | null;
  notes?: string | null;
}

export const NDT_CALIBRATION_STATUSES = ['valid', 'due_soon', 'overdue', 'never'] as const;
export type NdtCalibrationStatus = (typeof NDT_CALIBRATION_STATUSES)[number];

export const NDT_INSTRUMENT_KINDS = [
  'exposure_device',
  'survey_meter',
  'ut_flaw_detector',
  'yoke',
  'densitometer',
  'pmi_analyzer',
  'other',
] as const;
export type NdtInstrumentKind = (typeof NDT_INSTRUMENT_KINDS)[number];

export interface NdtInstrument {
  /** The equipment row's id. */
  id: string;
  name: string | null;
  type: string | null;
  serialNumber: string | null;
  manufacturer: string | null;
  model: string | null;
  status: string | null;
  specs: {
    id: string;
    instrumentKind: string;
    calibrationIntervalDays: number | null;
    notes: string | null;
    createdAt: string | null;
    updatedAt: string | null;
  } | null;
  latestCalibration: {
    id: string;
    calibratedOn: string | null;
    dueOn: string | null;
    certificateNumber: string | null;
    performedBy: string | null;
  } | null;
  calibrationStatus: NdtCalibrationStatus;
  daysUntilDue: number | null;
  alertTier: number | null;
}

export interface NdtCalibration {
  id: string;
  equipmentId: string;
  calibratedOn: string | null;
  dueOn: string | null;
  certificateNumber: string | null;
  performedBy: string | null;
  notes: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string | null;
}

export interface NdtInstrumentDetail extends NdtInstrument {
  calibrations: NdtCalibration[];
}

export interface NdtSpecsWrite {
  instrument_kind?: string;
  calibration_interval_days?: number | null;
  notes?: string | null;
}

export interface NdtCalibrationCreate {
  calibrated_on: string;
  due_on?: string | null;
  certificate_number?: string | null;
  performed_by?: string | null;
  notes?: string | null;
}

/**
 * A source's status. NDT's sources live on the SHARED radiation register since
 * api#1335 (RAD-N), so a source can be in any of the register's six statuses —
 * `lost_in_hole` and `abandoned_in_hole` are set through /api/v1/radiation,
 * never through NDT's own edit. GET /ndt-inspection/sources?status= accepts all six.
 */
export const NDT_SOURCE_STATUSES = ['in_service', 'in_storage', 'lost_in_hole', 'abandoned_in_hole', 'returned', 'disposed'] as const;
export type NdtSourceStatus = (typeof NDT_SOURCE_STATUSES)[number];
/** The statuses NDT's add / edit body may set (fieldiq-api ndt-catalog NDT_SOURCE_STATUSES). */
export const NDT_SOURCE_SETTABLE_STATUSES = ['in_service', 'in_storage', 'returned', 'disposed'] as const;
export const NDT_SOURCE_EVENTS = ['check_out', 'check_in', 'transfer'] as const;
export type NdtSourceEvent = (typeof NDT_SOURCE_EVENTS)[number];
/**
 * Every event a source's log can hold. NDT records only the three custody
 * events; the rest arrive from the shared register (a disposal NDT's edit
 * records, or a handling / lost / recovered event recorded under /radiation).
 */
export const NDT_REGISTER_SOURCE_EVENTS = [
  'check_out', 'check_in', 'transfer',
  'out_of_shield', 'in_hole', 'out_of_hole', 'in_shield',
  'lost_in_hole', 'recovered', 'abandoned_in_hole', 'disposed',
] as const;
export type NdtRegisterSourceEvent = (typeof NDT_REGISTER_SOURCE_EVENTS)[number];

/**
 * A source's leak-test standing: the expiry tiers plus `failed` — the newest
 * leak test on the shared register failed (api#1335). Kept apart from
 * NdtExpiryStatus so a cert or a qual can never read "Failed".
 */
export const NDT_LEAK_TEST_STATUSES = [...NDT_EXPIRY_STATUSES, 'failed'] as const;
export type NdtLeakTestStatus = (typeof NDT_LEAK_TEST_STATUSES)[number];

export interface NdtSource {
  id: string;
  isotope: string;
  isotopeLabel: string | null;
  halfLifeDays: number | null;
  serialNumber: string;
  deviceEquipmentId: string | null;
  deviceName: string | null;
  referenceActivity: number | null;
  referenceOn: string | null;
  /** Decayed to `currentActivityOn` (the tenant's today), in meta.units.activity. Computed by the API. */
  currentActivity: number | null;
  currentActivityOn: string | null;
  elapsedDays: number | null;
  licenceNumber: string | null;
  status: string;
  leakTest: { lastOn: string | null; dueOn: string | null; status: NdtLeakTestStatus; daysLeft: number | null };
  custody: {
    checkedOut: boolean;
    holderUserId: string | null;
    holderName: string | null;
    lastEvent: string | null;
    lastEventAt: string | null;
    location: string | null;
  };
  notes: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtSourceLog {
  id: string;
  sourceId: string;
  event: string;
  userId: string | null;
  userName: string | null;
  occurredAt: string | null;
  location: string | null;
  jobRef: string | null;
  surveyReadingUsvH: number | null;
  /**
   * The same reading in the tenant's unit (api#1142): mR/h on an imperial
   * tenant, µSv/h on a metric one. Optional because a read from before
   * api#1142 does not carry it; the screen then falls back to
   * surveyReadingUsvH in µSv/h.
   */
  surveyReading?: number | null;
  surveyReadingUnit?: string;
  notes: string | null;
  createdBy: string | null;
  createdAt: string | null;
}

export interface NdtSourceDetail extends NdtSource {
  logs: NdtSourceLog[];
}

export interface NdtSourceWrite {
  isotope?: string;
  serial_number?: string;
  device_equipment_id?: string | null;
  /** In meta.units.activity. */
  reference_activity?: number;
  reference_on?: string;
  licence_number?: string | null;
  status?: string;
  leak_test_last_on?: string | null;
  leak_test_due_on?: string | null;
  notes?: string | null;
}

export interface NdtSourceLogCreate {
  event: NdtSourceEvent;
  user_id?: string | null;
  location?: string | null;
  job_ref?: string | null;
  survey_reading_usv_h?: number | null;
  notes?: string | null;
}

export interface NdtDecay {
  sourceId: string;
  isotope: string;
  halfLifeDays: number;
  referenceOn: string;
  referenceActivity: number | null;
  on: string;
  elapsedDays: number;
  activity: number | null;
  activityGbq: number;
  unit: string;
}

export const NDT_DOSIMETER_KINDS = ['badge', 'tld', 'osl', 'drd', 'epd'] as const;

export interface NdtDose {
  id: string;
  userId: string;
  userName: string | null;
  periodStart: string;
  periodEnd: string;
  dosimeterKind: string;
  dosimeterSerial: string | null;
  deepDose: number | null;
  shallowDose: number | null;
  /** null = no investigation level configured; never a guessed false. */
  exceedsInvestigationLevel: boolean | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtDoseWrite {
  user_id?: string;
  period_start?: string;
  period_end?: string;
  dosimeter_kind?: string;
  dosimeter_serial?: string | null;
  deep_dose?: number;
  shallow_dose?: number | null;
  notes?: string | null;
}

// ─── GET /dashboard/ndt-compliance (api#1142) ───────────────────────────────
// Typed by hand from fieldiq-api src/lib/ndt-compliance.ts (NdtCompliance) at
// 000c62e5. DATA-gated, not vertical-gated: an area with no rows is null
// (absent, never zero), and a company with no NDT rows reads hasData:false
// with every area null. camelCase, like every NDT read.

export interface NdtComplianceExpiryCounts { valid: number; dueSoon: number; expired: number; none: number }
/** Welder quals add `lapsed`; `none` = no expiry recorded and continuity holding (as certs count it). */
export interface NdtComplianceQualCounts extends NdtComplianceExpiryCounts { lapsed: number }
export interface NdtComplianceCalCounts { valid: number; dueSoon: number; overdue: number; never: number }

export interface NdtComplianceSources {
  total: number;
  held: number;
  checkedOut: number;
  checkedOutLong: number;
  /** The threshold checkedOutLong is counted against, in hours. */
  checkedOutAlertHours: number;
  leakDueSoon: number;
  leakOverdue: number;
}

export interface NdtComplianceDose {
  /** null when the company has configured no investigation level — never a guessed 0. */
  overLevel: number | null;
  investigationLevelConfigured: boolean;
  /** Calendar day, 'YYYY-MM-DD'. */
  latestPeriodEnd: string | null;
  workers: number;
}

export interface NdtCompliance {
  hasData: boolean;
  today: string;
  certs: NdtComplianceExpiryCounts | null;
  welderQuals: NdtComplianceQualCounts | null;
  instruments: NdtComplianceCalCounts | null;
  sources: NdtComplianceSources | null;
  /** null when there are no dose records, OR when doseRestricted. */
  dose: NdtComplianceDose | null;
  /** The caller's role may not see every worker's dose. Not "no dose". */
  doseRestricted: boolean;
}

// ─── Batch 2: jobs, crew, welds, exams, indications (fieldiq-api#1144) ───────
// Typed by hand from fieldiq-api src/routes/ndt-inspection-jobs.routes.ts at
// 0987aa1b. OFFICE reads are camelCase. The FIELD surface (/field/*) returns
// the SAME objects with every key snake_cased — see NdtField* below. Lengths
// (diameter, wall, indication location/length/depth) are in meta.units.length.

/** The batch-2 catalog keys GET /catalog gained. Optional: an older read lacks them. */
export interface NdtCatalogB2 {
  examMethods?: NdtCatalogEntry[];
  techniques?: Record<string, NdtCatalogEntry[]>;
  /** method → the instrument kind it must name (RT: only with a gamma source). */
  examInstrumentKinds?: Record<string, string>;
  examCycles?: string[];
  examResults?: string[];
  jobStatuses?: string[];
  crewRoles?: NdtCatalogEntry[];
  jointTypes?: NdtCatalogEntry[];
  indicationTypes?: NdtCatalogEntry[];
}

export const NDT_JOB_STATUSES = ['scheduled', 'active', 'complete', 'cancelled'] as const;
export type NdtJobStatus = (typeof NDT_JOB_STATUSES)[number];
export const NDT_WELD_STANDINGS = ['unexamined', 'pending', 'accept', 'repair', 'cutout'] as const;
export type NdtWeldStanding = (typeof NDT_WELD_STANDINGS)[number];
export const NDT_EXAM_RESULTS = ['pending', 'accept', 'repair', 'cutout'] as const;
export type NdtExamResult = (typeof NDT_EXAM_RESULTS)[number];
export const NDT_EXAM_CYCLES = ['original', 'R1', 'R2', 'cutout'] as const;
export type NdtExamCycle = (typeof NDT_EXAM_CYCLES)[number];
export const NDT_QUAL_STATUSES = ['qualified', 'not_qualified', 'no_welder'] as const;
export const NDT_QUAL_REASONS = [
  'no_quals', 'code_mismatch', 'process_mismatch', 'position_not_covered',
  'diameter_out_of_range', 'wall_out_of_range', 'qual_expired', 'continuity_lapsed',
] as const;
export type NdtQualReason = (typeof NDT_QUAL_REASONS)[number];

/** Computed by the API: rate null when nothing is examined — absent, never 0. */
export interface NdtRepairRate {
  examined: number;
  repaired: number;
  rate: number | null;
  /** Fewer than 5 examined welds — a comparison is meaningless below that. */
  smallSample: boolean;
}

export interface NdtWeldCounts {
  total: number;
  unexamined: number;
  pending: number;
  accept: number;
  repair: number;
  cutout: number;
}

export interface NdtJob {
  id: string;
  jobNumber: string;
  status: string;
  customerId: string;
  customerName: string | null;
  lineRef: string | null;
  projectRef: string | null;
  externalProjectRef: string | null;
  externalSource: string | null;
  poNumber: string | null;
  afeNumber: string | null;
  locationName: string | null;
  lsd: string | null;
  provinceState: string | null;
  latitude: number | null;
  longitude: number | null;
  acceptanceCode: string | null;
  acceptanceCodeLabel: string | null;
  ticketMode: string | null;
  currency: string | null;
  scope: string | null;
  /** Calendar day, YYYY-MM-DD. */
  scheduledDate: string | null;
  cancelReason: string | null;
  startedAt: string | null;
  completedAt: string | null;
  crewCount: number;
  weldCounts: NdtWeldCounts;
  repairRate: NdtRepairRate;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtCrew {
  id: string;
  jobId: string;
  userId: string;
  userName: string | null;
  crewRole: string;
  crewRoleLabel: string | null;
  startOn: string | null;
  endOn: string | null;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtJobDetail extends NdtJob {
  crew: NdtCrew[];
  /**
   * The quote this job was booked from (POST /quotes/:id/create-ndt-job), on
   * GET /ndt-inspection/jobs/:id once the API sends it; null for a job booked
   * directly. Absent on an older payload, which reads the same as null.
   */
  sourceQuote?: { id: string; number: string | null } | null;
}

export interface NdtJobWrite {
  customer_id?: string;
  line_ref?: string | null;
  project_ref?: string | null;
  external_project_ref?: string | null;
  external_source?: string | null;
  po_number?: string | null;
  afe_number?: string | null;
  location_name?: string | null;
  lsd?: string | null;
  province_state?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  acceptance_code?: string | null;
  ticket_mode?: NdtTicketMode;
  scope?: string | null;
  scheduled_date?: string | null;
  status?: 'active' | 'complete' | 'cancelled';
  cancel_reason?: string;
}

export interface NdtCrewWrite {
  user_id?: string;
  crew_role?: string;
  start_on?: string | null;
  end_on?: string | null;
  notes?: string | null;
}

export interface NdtWelderQualification {
  status: string;
  qualId: string | null;
  reasons: string[];
  checkedOn: string | null;
}

export interface NdtWeld {
  id: string;
  weldUid: string;
  jobId: string;
  jobNumber: string | null;
  weldNumber: string;
  lineRef: string | null;
  spool: string | null;
  station: string | null;
  kp: string | null;
  diameter: number | null;
  wall: number | null;
  material: string | null;
  process: string | null;
  processLabel: string | null;
  position: string | null;
  welderId: string | null;
  welderName: string | null;
  welderStamp: string | null;
  weldDate: string | null;
  jointType: string | null;
  jointTypeLabel: string | null;
  externalWeldRef: string | null;
  notes: string | null;
  standing: string;
  examCount: number;
  welderQualification: NdtWelderQualification;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtIndication {
  id: string;
  examId: string;
  indicationType: string;
  indicationTypeLabel: string | null;
  location: number | null;
  clockPosition: number | null;
  length: number | null;
  depth: number | null;
  disposition: string;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtExam {
  id: string;
  weldId: string;
  weldNumber: string | null;
  jobId: string;
  method: string;
  methodLabel: string | null;
  technique: string | null;
  techniqueLabel: string | null;
  cycle: string;
  result: string;
  repairWelderId: string | null;
  repairWelderName: string | null;
  technicianId: string;
  technicianName: string | null;
  technicianCertId: string | null;
  technicianLevel: number | null;
  radiationCertId: string | null;
  instrumentEquipmentId: string | null;
  instrumentName: string | null;
  calibrationId: string | null;
  sourceId: string | null;
  sourceSerial: string | null;
  sourceIsotope: string | null;
  /** Decayed to the exam day by the API, in meta.units.activity. */
  sourceActivity: number | null;
  shotsCount: number | null;
  filmsCount: number | null;
  filmDensity: number | null;
  iqi: string | null;
  sensitivityPercent: number | null;
  examinedAt: string | null;
  /** examinedAt's calendar day in the tenant zone. */
  examDay: string | null;
  notes: string | null;
  indications: NdtIndication[];
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtWeldDetail extends NdtWeld {
  exams: NdtExam[];
}

export interface NdtWeldWrite {
  weld_number?: string;
  line_ref?: string | null;
  spool?: string | null;
  station?: string | null;
  kp?: string | null;
  /** In meta.units.length. */
  diameter?: number | null;
  wall?: number | null;
  material?: string | null;
  process?: string | null;
  position?: string | null;
  welder_id?: string | null;
  weld_date?: string | null;
  joint_type?: string | null;
  external_weld_ref?: string | null;
  notes?: string | null;
}

export interface NdtIndicationWrite {
  indication_type: string;
  disposition: 'accept' | 'reject';
  location?: number | null;
  clock_position?: number | null;
  length?: number | null;
  depth?: number | null;
  notes?: string | null;
}

export interface NdtExamWrite {
  method?: string;
  cycle?: string | null;
  technique?: string | null;
  result?: NdtExamResult;
  technician_id?: string | null;
  instrument_equipment_id?: string | null;
  source_id?: string | null;
  repair_welder_id?: string | null;
  shots_count?: number | null;
  films_count?: number | null;
  film_density?: number | null;
  iqi?: string | null;
  sensitivity_percent?: number | null;
  /** ISO with a zone; omitted = now (the API's clock). */
  examined_at?: string;
  notes?: string | null;
  indications?: NdtIndicationWrite[];
}

/** A shot-guard warning: never a refusal. `daysLeft` from the exam day. */
export interface NdtShotWarning {
  code: string;
  field: string;
  message: string;
  daysLeft: number;
}

export interface NdtShotFailure {
  code: string;
  field: string;
  error: string;
}

export interface NdtRepairRates {
  overall: NdtRepairRate;
  byJob: (NdtRepairRate & { jobId: string; jobNumber: string })[];
  byWelder: (NdtRepairRate & { welderId: string; welderName: string | null; welderStamp: string | null })[];
}

// ─── The field surface: the same objects, snake_case keys ────────────────────

export interface NdtFieldRepairRate { examined: number; repaired: number; rate: number | null; small_sample: boolean }

export interface NdtFieldJob {
  id: string;
  job_number: string;
  status: string;
  customer_name: string | null;
  line_ref: string | null;
  project_ref: string | null;
  location_name: string | null;
  lsd: string | null;
  acceptance_code: string | null;
  acceptance_code_label: string | null;
  scheduled_date: string | null;
  scope: string | null;
  crew_count: number;
  weld_counts: NdtWeldCounts;
  repair_rate: NdtFieldRepairRate;
  /** per_day | per_job — the job's ticket-mode snapshot (batch 4 reads it to offer "Close out today"). */
  ticket_mode?: string | null;
}

export interface NdtFieldIndication {
  id: string;
  indication_type: string;
  indication_type_label: string | null;
  location: number | null;
  clock_position: number | null;
  length: number | null;
  depth: number | null;
  disposition: string;
  notes: string | null;
}

export interface NdtFieldExam {
  id: string;
  weld_id: string;
  method: string;
  method_label: string | null;
  technique: string | null;
  technique_label: string | null;
  cycle: string;
  result: string;
  technician_id: string;
  technician_name: string | null;
  technician_level: number | null;
  instrument_name: string | null;
  source_serial: string | null;
  examined_at: string | null;
  exam_day: string | null;
  indications: NdtFieldIndication[];
}

export interface NdtFieldWeld {
  id: string;
  weld_uid: string;
  job_id: string;
  weld_number: string;
  line_ref: string | null;
  diameter: number | null;
  wall: number | null;
  process: string | null;
  process_label: string | null;
  position: string | null;
  welder_id: string | null;
  welder_name: string | null;
  welder_stamp: string | null;
  weld_date: string | null;
  standing: string;
  exam_count: number;
  welder_qualification: { status: string; qual_id: string | null; reasons: string[]; checked_on: string | null };
  exams?: NdtFieldExam[];
}

export interface NdtFieldCrew {
  id: string;
  user_id: string;
  user_name: string | null;
  crew_role: string;
  crew_role_label: string | null;
}

export interface NdtFieldJobDetail extends NdtFieldJob {
  crew: NdtFieldCrew[];
  welds: NdtFieldWeld[];
}

/** The field meta is snake_cased too: unit_system, time_zone, today, units. */
export interface NdtFieldMeta {
  unit_system: string | null;
  units: NdtUnits | null;
  time_zone: string | null;
  today: string | null;
}

// ═══ Batch 3: inspection reports (fieldiq-api#1152) ══════════════════════════
//
// Typed by hand from routes/ndt-inspection-reports.routes.ts and
// lib/ndt-report.ts (shapeReport / shapeLine / shapeHeader) at fb85e65 — the
// manifest records every report route as 403 for the probe tenant, so nothing
// here is generated. Lengths and activity arrive in the tenant's units
// (`header.units`, `meta.units`); nothing on the screen converts.

export const NDT_REPORT_KINDS = ['final', 'interim', 'daily'] as const;
export type NdtReportKind = (typeof NDT_REPORT_KINDS)[number];
export const NDT_REPORT_STATUSES = ['draft', 'issued', 'void'] as const;
export type NdtReportStatus = (typeof NDT_REPORT_STATUSES)[number];

/** Every number the report states — computed by the API, never by the LLM or this screen. */
export interface NdtReportStats {
  weldsCovered: number;
  examsCovered: number;
  results: { accept: number; repair: number; cutout: number };
  byMethod: { method: string; methodLabel: string | null; exams: number; accept: number; repair: number; cutout: number }[];
  cycles: { original: number; R1: number; R2: number; cutout: number };
  repairRate: NdtRepairRate;
  indications: { total: number; rejectable: number; byType: { type: string; label: string | null; count: number }[] };
  repairedWelds: string[];
  cutoutWelds: string[];
  technicians: number;
  firstExamDay: string | null;
  lastExamDay: string | null;
  pendingExcluded: number;
  smallSample: boolean;
}

export interface NdtReportHeader {
  job: {
    id: string;
    jobNumber: string;
    customerName: string;
    lineRef: string | null;
    projectRef: string | null;
    externalProjectRef: string | null;
    poNumber: string | null;
    afeNumber: string | null;
    locationName: string | null;
    lsd: string | null;
    provinceState: string | null;
    scope: string | null;
    scheduledDate: string | null;
    startedAt: string | null;
    completedAt: string | null;
    status: string;
  };
  acceptanceCode: string | null;
  acceptanceCodeLabel: string | null;
  country: string | null;
  unitSystem: 'metric' | 'imperial';
  units: NdtUnits;
  timeZone: string;
  technicians: {
    userId: string;
    name: string | null;
    certs: {
      method: string;
      methodLabel: string | null;
      level: number | null;
      scheme: string;
      schemeLabel: string | null;
      certificateNumber: string | null;
      expiresOn: string | null;
    }[];
  }[];
  instruments: {
    equipmentId: string;
    name: string | null;
    serialNumber: string | null;
    calibratedOn: string | null;
    dueOn: string | null;
    calibrationCertificate: string | null;
  }[];
  /** activityAtExams: [min, max] in the tenant's activity unit; [] when none recorded. */
  sources: { sourceId: string; isotope: string; serialNumber: string; activityAtExams: number[] }[];
}

export interface NdtReportIndication {
  indicationType: string;
  indicationTypeLabel: string | null;
  disposition: string;
  location: number | null;
  clockPosition: number | null;
  length: number | null;
  depth: number | null;
  notes: string | null;
}

export interface NdtReportLine {
  weldId: string;
  examId: string;
  sortOrder: number;
  weldUid: string;
  weldNumber: string;
  lineRef: string | null;
  station: string | null;
  diameter: number | null;
  wall: number | null;
  welderName: string | null;
  welderStamp: string | null;
  method: string;
  methodLabel: string | null;
  technique: string | null;
  techniqueLabel: string | null;
  cycle: string;
  result: 'accept' | 'repair' | 'cutout';
  technicianId: string | null;
  technicianName: string | null;
  technicianLevel: number | null;
  examinedAt: string | null;
  examDay: string;
  indications: NdtReportIndication[];
}

export interface NdtReport {
  id: string;
  /** null until issue — a draft is numbered when it is issued. */
  reportNumber: string | null;
  jobId: string;
  jobNumber: string;
  jobStatus: string;
  customerName: string;
  kind: NdtReportKind;
  kindLabel: string;
  status: NdtReportStatus;
  periodFrom: string | null;
  periodTo: string | null;
  acceptanceCode: string | null;
  acceptanceCodeLabel: string | null;
  title: string | null;
  customerContactName: string | null;
  customerContactEmail: string | null;
  externalRef: string | null;
  externalSource: string | null;
  /** The AI's text. NEVER printed, never shown as approved. */
  summaryDraft: string | null;
  summaryDraftGeneratedAt: string | null;
  /** The draft was written from different numbers than the report now states. */
  summaryDraftStale: boolean;
  /** The human-approved text — the only summary the PDF prints. */
  summaryFinal: string | null;
  /** A user id. */
  summaryApprovedBy: string | null;
  summaryApprovedAt: string | null;
  stats: NdtReportStats;
  header: NdtReportHeader;
  lineCount: number;
  /** Present on GET /reports/:id and every write; absent on the list. */
  lines?: NdtReportLine[];
  issuedBy: string | null;
  issuedAt: string | null;
  issuedSignatoryName: string | null;
  hasPdf: boolean;
  voidReason: string | null;
  voidedBy: string | null;
  voidedAt: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/** POST /reports body. period_* are YYYY-MM-DD; a final takes none, a daily takes period_from only. */
export interface NdtReportCreate {
  job_id: string;
  kind: NdtReportKind;
  period_from?: string;
  period_to?: string;
  title?: string;
}

export interface NdtReportUpdate {
  title?: string | null;
  customer_contact_name?: string | null;
  customer_contact_email?: string | null;
  external_ref?: string | null;
  external_source?: string | null;
  period_from?: string | null;
  period_to?: string | null;
}

export interface NdtReportWarning {
  code: string;
  message: string;
}
